<template>
  <div class="w-full">
    <!-- Page Header -->
    <div class="flex items-center justify-between mb-5">
      <div class="flex items-center gap-3">
        <div
          style="
            width: 4px;
            height: 28px;
            background: linear-gradient(180deg, #7367f0, #ce9ffc);
            border-radius: 4px;
          "
        ></div>
        <h4 class="font-bold text-lg" style="color: #444; margin: 0">
          {{ id ? "Edit" : "Create" }} {{ title }}
        </h4>
      </div>
    </div>

    <!-- Basic Information Card -->
    <vx-card
      class="cluster-form-card mb-5"
      style="box-shadow: 0 2px 12px rgba(115, 103, 240, 0.08)"
    >
      <div class="section-header">
        <span class="dot"></span>
        Basic Information
      </div>
      <div class="vx-row">
        <!-- Name -->
        <div class="vx-col w-full md:w-1/2 mb-4">
          <label class="vs-input--label">
            <span style="color: #ea5455">*</span> Name
          </label>
          <vs-input
            :class="['w-full', { 'input-readonly': !!id }]"
            name="Name"
            v-model="data.name"
            v-validate="'required'"
            placeholder="Enter cluster name"
            :disabled="!!id"
          />
          <span class="text-danger text-sm" v-show="errors.has('Name')">
            {{ errors.first("Name") }}
          </span>
        </div>

        <!-- Type -->
        <div class="vx-col w-full md:w-1/2 mb-4">
          <label class="vs-input--label">
            <span style="color: #ea5455">*</span> Type
          </label>
          <vs-input
            :class="['w-full', { 'input-readonly': !!id }]"
            name="Type"
            v-model="data.type"
            v-validate="'required'"
            placeholder="Enter type (e.g. standard)"
            :disabled="!!id"
          />
          <span class="text-danger text-sm" v-show="errors.has('Type')">
            {{ errors.first("Type") }}
          </span>
        </div>

        <!-- Status -->
        <div class="vx-col w-full mb-2">
          <label class="vs-input--label mb-2 block">Status</label>
          <div class="flex items-center gap-3">
            <vs-switch
              v-model="data.is_active"
              color="success"
              :true-value="true"
              :false-value="false"
            />
            <span :class="data.is_active ? 'badge-active' : 'badge-inactive'">
              <span
                style="
                  width: 7px;
                  height: 7px;
                  border-radius: 50%;
                  background: currentColor;
                  display: inline-block;
                "
              ></span>
              {{ data.is_active ? "Active" : "Inactive" }}
            </span>
          </div>
        </div>
      </div>
    </vx-card>

    <!-- Customer Cluster Card -->
    <vx-card
      class="cluster-form-card mb-5"
      style="box-shadow: 0 2px 12px rgba(115, 103, 240, 0.08)"
    >
      <div class="section-header">
        <span class="dot"></span>
        Customer Cluster
        <span class="customer-count-badge">{{
          data.customerCluster.length
        }}</span>
      </div>

      <!-- Customer Table -->

      <!-- Error Customer Banner -->
      <transition name="slide-fade">
        <div v-if="errorCustomerCodes.length > 0" class="error-customer-banner">
          <div class="error-banner-icon">
            <feather-icon icon="AlertTriangleIcon" svgClasses="w-6 h-6" />
          </div>
          <div class="error-banner-content">
            <div class="error-banner-title">
              Save Failed — Customer Conflict Detected
            </div>
            <div class="error-banner-desc">
              <strong>{{ errorCustomerCodes.length }} customer(s)</strong>
              highlighted below could not be saved. They may already belong to
              another cluster or have a data conflict. Please review and remove
              them before retrying.
            </div>
            <div class="error-banner-codes">
              <span
                v-for="code in errorCustomerCodes"
                :key="code"
                class="error-code-pill"
                >{{ code }}</span
              >
            </div>
          </div>
        </div>
      </transition>

      <div style="overflow-x: auto">
        <table class="customer-table" v-if="data.customerCluster.length > 0">
          <thead>
            <tr>
              <th style="width: 50px">#</th>
              <th>Customer Code</th>
              <th>Customer Name</th>
              <th style="width: 120px">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(customer, index) in data.customerCluster"
              :key="customer.id || index"
              :class="{
                'row-error': errorCustomerCodes.includes(
                  customer.customer_code
                ),
              }"
            >
              <td style="color: #aaa; font-size: 0.8rem">{{ index + 1 }}</td>
              <td>
                <span
                  :class="[
                    'badge-code',
                    errorCustomerCodes.includes(customer.customer_code)
                      ? 'badge-code-error'
                      : '',
                  ]"
                  >{{ customer.customer_code }}</span
                >
              </td>
              <td>
                <div class="customer-name-cell">
                  <span
                    :class="{
                      'customer-name-error': errorCustomerCodes.includes(
                        customer.customer_code
                      ),
                    }"
                    >{{ customer.customer_name }}</span
                  >
                  <span
                    v-if="errorCustomerCodes.includes(customer.customer_code)"
                    class="warning-chip"
                  >
                    <feather-icon
                      icon="AlertTriangleIcon"
                      svgClasses="w-3 h-3"
                    />
                    Conflict
                  </span>
                </div>
              </td>
              <td>
                <button
                  class="btn-remove"
                  @click="removeCustomer(index)"
                  title="Remove"
                >
                  <feather-icon icon="TrashIcon" svgClasses="w-3 h-3" />
                  Remove
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Empty State -->
        <div class="empty-state" v-else>
          <div
            style="display: flex; justify-content: center; margin-bottom: 8px"
          >
            <feather-icon
              icon="UsersIcon"
              svgClasses="w-10 h-10"
              style="color: #d0cff0"
            />
          </div>
          <p style="margin: 0">No customers added yet.</p>
          <p style="font-size: 0.8rem; color: #ccc; margin: 4px 0 0">
            Use the panel below to add customers to this cluster.
          </p>
        </div>
      </div>

      <!-- Add Customer Panel -->
      <div class="add-customer-panel">
        <div class="panel-title">
          <feather-icon
            icon="PlusCircleIcon"
            svgClasses="w-4 h-4 inline mr-1"
          />
          Add Customer
        </div>
        <div class="vx-row" style="align-items: flex-end">
          <div class="vx-col flex-1">
            <label
              class="vs-input--label mb-1 block"
              style="font-size: 0.82rem"
            >
              you can search by customer code or name. For multiple customers,
              separate with semicolon (;),example: CUST001;CUST002;CUST003
            </label>
            <multiselect
              v-model="newCustomer.selected"
              :options="newCustomer.options"
              placeholder="Type to search customer..."
              track-by="customer_id"
              label="name"
              :max-height="200"
              :allow-empty="true"
              :multiple="true"
              :internal-search="false"
              :close-on-select="false"
              :custom-label="customerLabel"
              @search-change="handleCustomerSearch"
            />
          </div>
          <div style="width: auto; padding-left: 12px; padding-bottom: 2px">
            <vs-button
              color="primary"
              icon-pack="feather"
              icon="icon-plus"
              @click="addCustomer"
              :disabled="
                !newCustomer.selected || newCustomer.selected.length === 0
              "
            >
              Add
            </vs-button>
          </div>
        </div>
      </div>
    </vx-card>

    <!-- Action Buttons -->
    <div class="flex items-center gap-3">
      <vs-button
        color="primary"
        icon-pack="feather"
        icon="icon-save"
        @click="handleSubmit"
      >
        {{ id ? "Update" : "Save" }}
      </vs-button>
      <vs-button
        type="border"
        color="danger"
        icon-pack="feather"
        icon="icon-x"
        @click="handleClose"
      >
        Close
      </vs-button>
    </div>
  </div>
</template>
<script>
import _ from "lodash";
import Multiselect from "vue-multiselect";

export default {
  components: { Multiselect },
  props: {
    id: { type: Number },
    isDefault: { type: Number },
    url: { type: String },
    title: { type: String },
  },
  data() {
    return this.initialState();
  },
  methods: {
    initialState() {
      return {
        data: {
          id: null,
          name: "",
          type: "",
          is_active: true,
          customerCluster: [],
        },
        newCustomer: {
          selected: [],
          options: [],
        },
        errorCustomerCodes: [],
      };
    },
    customerLabel(opt) {
      return opt.code + " - " + opt.name;
    },
    handleSubmit() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          if (this.data.customerCluster.length === 0) {
            this.$vs.notify({
              color: "warning",
              title: "Validation Error",
              text: "Customer Cluster cannot be empty. Please add at least one customer.",
              position: "top-right",
              iconPack: "feather",
              icon: "icon-alert-circle",
            });
            return;
          }
          this.errorCustomerCodes = [];
          this.$vs.loading();
          if (this.id) {
            this.putData();
          } else {
            this.postData();
          }
        }
      });
    },
    paramData() {
      return {
        name: this.data.name,
        is_active: this.data.is_active,
        type: this.data.type ? this.data.type : "",
        customer_clusters: this.data.customerCluster.map((c) => ({
          customer_id: c.customer_id,
          customer_name: c.customer_name,
          customer_code: c.customer_code,
        })),
      };
    },
    postData() {
      this.$http
        .post(this.url, this.paramData())
        .then((resp) => {
          this.$vs.loading.close();
          if (resp.code == 200) {
            this.handleClose();
            this.$vs.notify({
              color: "success",
              title: "Success",
              text: "New " + this.title + " Created",
              position: "top-right",
              iconPack: "feather",
              icon: "icon-check-circle",
            });
          } else {
            this.errorCustomerCodes =
              resp.data && resp.data.customer_codes
                ? resp.data.customer_codes
                : [];
            this.$vs.notify({
              color: "danger",
              title: "Error",
              text: resp.message,
              position: "top-right",
              iconPack: "feather",
              icon: "icon-x-circle",
            });
          }
        })
        .catch((error) => {
          this.$vs.loading.close();
          console.log(error);
        });
    },
    putData() {
      this.$http
        .put(this.url + "/" + this.id, this.paramData())
        .then((resp) => {
          this.$vs.loading.close();
          if (resp.code == 200) {
            this.handleClose();
            this.$vs.notify({
              color: "success",
              title: "Success",
              text: this.title + " Updated",
              position: "top-right",
              iconPack: "feather",
              icon: "icon-check-circle",
            });
          } else {
            this.errorCustomerCodes =
              resp.data && resp.data.customer_codes
                ? resp.data.customer_codes
                : [];
            this.$vs.notify({
              color: "danger",
              title: "Error",
              text: resp.message,
              position: "top-right",
              iconPack: "feather",
              icon: "icon-x-circle",
            });
          }
        })
        .catch((error) => {
          this.$vs.loading.close();
          console.log(error);
        });
    },
    handleClose() {
      window.scrollTo(0, 0);
      this.$emit("close");
    },
    getData() {
      this.$vs.loading();
      this.$http
        .get(this.url + "/" + this.id)
        .then((resp) => {
          this.$vs.loading.close();
          if (resp.code == 200) {
            const d = resp.data;
            this.data.id = d.ID;
            this.data.name = d.name;
            this.data.type = d.type;
            this.data.is_active = d.is_active;
            this.data.customerCluster = d.customer_cluster
              ? d.customer_cluster.map((c) => ({
                  id: c.ID,
                  customer_id: c.customer_id,
                  customer_name: c.customer_name,
                  customer_code: c.customer_code,
                }))
              : [];
          } else {
            this.$vs.notify({
              color: "danger",
              title: "Error",
              text: resp.message,
              position: "top-right",
              iconPack: "feather",
              icon: "icon-x-circle",
            });
          }
        })
        .catch((error) => {
          this.$vs.loading.close();
          console.log(error);
        });
    },
    handleCustomerSearch: _.debounce(function (val) {
      // Don't clear options when search is empty or too short —
      // multiselect emits search-change("") after each selection,
      // clearing options here would cause options to vanish mid-click.
      if (!val || val.length < 3) {
        return;
      }
      if (val.includes(";")) {
        const codes = val
          .split(";")
          .map((s) => s.trim())
          .filter((s) => s.length > 0);
        if (codes.length > 0) {
          this.fetchCustomerOptions(null, codes);
        }
        return;
      }
      this.fetchCustomerOptions(val);
    }, 500),
    async fetchCustomerOptions(search, codes) {
      try {
        const params = { length: 20, page: 1 };
        if (codes && codes.length > 0) {
          params.code = codes.join(";");
        } else {
          params.search = search || "";
        }
        const resp = await this.$http.get(this.url + "/customers", { params });
        if (resp.code == 200) {
          console.log("Fetched customers:", resp.data);
          resp.data = resp.data.map((c) => ({
            ...c,
            customer_id: c.id,
          }));
          this.newCustomer.options = resp.data || [];
          console.log("Customer options updated:", this.newCustomer.options);
        }
      } catch (e) {
        console.log(e);
      }
    },
    addCustomer() {
      if (!this.newCustomer.selected || this.newCustomer.selected.length === 0)
        return;
      const duplicates = [];
      const added = [];
      this.newCustomer.selected.forEach((c) => {
        const already = this.data.customerCluster.find(
          (x) => x.customer_id === c.customer_id
        );
        if (already) {
          duplicates.push(c.code || c.customer_code);
        } else {
          this.data.customerCluster.push({
            id: null,
            customer_id: c.customer_id,
            customer_name: c.name,
            customer_code: c.code,
          });
          added.push(c.code || c.name);
        }
      });
      if (duplicates.length > 0) {
        this.$vs.notify({
          color: "warning",
          title: "Duplicate Skipped",
          text: duplicates.join(", ") + " already in cluster.",
          position: "top-right",
          iconPack: "feather",
          icon: "icon-alert-circle",
        });
      }
      if (added.length > 0) {
        this.$vs.notify({
          color: "success",
          title: "Added",
          text: added.length + " customer(s) added.",
          position: "top-right",
          iconPack: "feather",
          icon: "icon-check-circle",
        });
      }
      this.newCustomer.selected = [];
    },
    removeCustomer(index) {
      this.$vs.dialog({
        type: "confirm",
        color: "danger",
        title: "Remove Customer",
        text: "Are you sure you want to remove this customer from the cluster?",
        accept: () => {
          const removed = this.data.customerCluster[index];
          this.data.customerCluster.splice(index, 1);
          if (removed && removed.customer_code) {
            const i = this.errorCustomerCodes.indexOf(removed.customer_code);
            if (i !== -1) this.errorCustomerCodes.splice(i, 1);
          }
        },
      });
    },
  },
  mounted() {
    if (this.id) {
      this.getData();
    }
  },
  computed: {},
  watch: {
    id(v) {
      if (v) {
        this.getData();
      } else {
        Object.assign(this.$data, this.initialState());
      }
    },
  },
};
</script>
<style>
.input-readonly > div > .vs-inputx {
  background-color: #e6e6e6;
}
.cluster-form-card {
  border-radius: 12px;
}
.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
  font-weight: 600;
  color: #444;
  margin-bottom: 16px;
  padding-bottom: 10px;
  border-bottom: 2px solid #f0f0f0;
}
.section-header .dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: linear-gradient(135deg, #7367f0, #ce9ffc);
  flex-shrink: 0;
}
.customer-table {
  width: 100%;
  border-collapse: collapse;
}
.customer-table thead tr {
  background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%);
  color: #fff;
}
.customer-table thead th {
  padding: 10px 14px;
  text-align: left;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.03em;
}
.customer-table thead th:first-child {
  border-radius: 8px 0 0 0;
}
.customer-table thead th:last-child {
  border-radius: 0 8px 0 0;
  text-align: center;
}
.customer-table tbody tr {
  transition: background 0.15s;
  border-bottom: 1px solid #f0f0f0;
}
.customer-table tbody tr:hover {
  background: #f7f5ff;
}
.customer-table tbody td {
  padding: 10px 14px;
  font-size: 0.87rem;
  color: #444;
  vertical-align: middle;
}
.customer-table tbody td:last-child {
  text-align: center;
}
.badge-code {
  display: inline-block;
  background: #ede9ff;
  color: #7367f0;
  font-weight: 600;
  font-size: 0.78rem;
  padding: 2px 10px;
  border-radius: 20px;
  letter-spacing: 0.02em;
}
.badge-active {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #e8f8f0;
  color: #28c76f;
  font-weight: 600;
  font-size: 0.78rem;
  padding: 3px 12px;
  border-radius: 20px;
}
.badge-inactive {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #fff0f0;
  color: #ea5455;
  font-weight: 600;
  font-size: 0.78rem;
  padding: 3px 12px;
  border-radius: 20px;
}
.btn-remove {
  background: #fff0f0;
  color: #ea5455;
  border: none;
  border-radius: 6px;
  padding: 5px 12px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  transition: background 0.15s, color 0.15s;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.btn-remove:hover {
  background: #ea5455;
  color: #fff;
}
.empty-state {
  text-align: center;
  padding: 32px 0;
  color: #b0b0b0;
  font-size: 0.9rem;
}
.add-customer-panel {
  background: #f7f5ff;
  border: 1.5px dashed #c5bef5;
  border-radius: 10px;
  padding: 16px;
  margin-top: 16px;
}
.add-customer-panel .panel-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #7367f0;
  margin-bottom: 12px;
}
.customer-count-badge {
  display: inline-block;
  background: #7367f0;
  color: #fff;
  border-radius: 12px;
  padding: 1px 10px;
  font-size: 0.78rem;
  font-weight: 700;
  margin-left: 8px;
}

/* ── Error / Conflict Styles ── */
.row-error {
  background: #fff5f5 !important;
}
.row-error:hover {
  background: #ffecec !important;
}
.badge-code-error {
  background: #fde8e8 !important;
  color: #ea5455 !important;
  border: 1px solid #f5c6cb;
}
.customer-name-cell {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}
.customer-name-error {
  color: #ea5455;
  font-weight: 600;
}
.warning-chip {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: #fff0f0;
  color: #ea5455;
  border: 1px solid #f5c6cb;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 12px;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}
.error-customer-banner {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  background: linear-gradient(135deg, #fff8f8 0%, #fff2f2 100%);
  border: 1.5px solid #f5c6cb;
  border-left: 5px solid #ea5455;
  border-radius: 10px;
  padding: 14px 18px;
  margin-bottom: 16px;
  animation: bannerIn 0.3s ease;
}
@keyframes bannerIn {
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.error-banner-icon {
  color: #ea5455;
  flex-shrink: 0;
  margin-top: 1px;
}
.error-banner-content {
  flex: 1;
}
.error-banner-title {
  font-weight: 700;
  color: #ea5455;
  font-size: 0.9rem;
  margin-bottom: 4px;
}
.error-banner-desc {
  font-size: 0.82rem;
  color: #c0392b;
  line-height: 1.6;
  margin-bottom: 8px;
}
.error-banner-codes {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}
.error-code-pill {
  display: inline-block;
  background: #fde8e8;
  color: #c0392b;
  border: 1px solid #f5c6cb;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 20px;
  font-family: monospace;
  letter-spacing: 0.04em;
}
/* Slide-fade transition */
.slide-fade-enter-active {
  transition: all 0.3s ease;
}
.slide-fade-leave-active {
  transition: all 0.2s ease;
}
.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateY(-8px);
  opacity: 0;
}
</style>
